import React, { useEffect, useState } from 'react';
import {
  Box, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TableSortLabel, Tooltip, Typography
} from '@mui/material';
import { ChevronRight as OpenIcon } from '@mui/icons-material';
import { fmtMoney } from '../../Documents/format';
import { fmtInt, fmtQty } from '../../OnlineJournal/format';
import { Origin, PharmacyRow, PharmacySort, PharmacySortKey, fmtDistance, hasCoords } from '../remainsCalc';
import { BORDER, headCellSx, panelSx, rowSx, tableContainerSx } from '../remainsStyles';
import { ExpiryCell, HoursCell, PharmacyName, RouteLink, Value } from './RemainsCommon';

interface Props {
  rows: PharmacyRow[];
  /** Аптек с остатком до фильтров списка */
  total: number;
  sort: PharmacySort;
  loading: boolean;
  withSums: boolean;
  withContacts: boolean;
  /** Точка отсчёта: есть - колонка «Расстояние» с маршрутом от неё */
  origin: Origin | null;
  hasFilters: boolean;
  onSort: (key: PharmacySortKey) => void;
  onOpen: (guid: string) => void;
  onResetFilters: () => void;
}

interface Column {
  id: string;
  label: string;
  width: number;
  align?: 'right';
  sort?: PharmacySortKey;
  tip?: string;
}

const PAGE_SIZES = [25, 50, 100];
const ACTIONS_WIDTH = 44;

function columnsFor(withSums: boolean, withContacts: boolean, withDistance: boolean): Column[] {
  return [
    { id: 'pharmacy', label: 'Аптека', width: 220, sort: 'num' },
    { id: 'region', label: 'Регион', width: 120 },
    { id: 'address', label: 'Адрес', width: 220 },
    ...(withContacts ? [{ id: 'phone', label: 'Телефон', width: 140, tip: 'Рабочий телефон аптеки из её профиля' }] : []),
    { id: 'hours', label: 'Время работы', width: 170, tip: 'Режим работы из профиля аптеки; «открыта» - по времени Алматы' },
    ...(withDistance ? [{ id: 'distance', label: 'Расстояние', width: 105, align: 'right' as const, sort: 'distance' as const, tip: 'По прямой от точки отсчёта; нажмите на расстояние - маршрут в картах' }] : []),
    { id: 'positions', label: 'Позиций', width: 85, align: 'right', sort: 'positions' },
    { id: 'units', label: 'Кол-во', width: 90, align: 'right', sort: 'units' },
    ...(withSums ? [{ id: 'amount', label: 'Сумма', width: 120, align: 'right' as const, sort: 'amount' as const }] : []),
    { id: 'expiry', label: 'Ближ. срок', width: 125, sort: 'expiry', tip: 'По журналу сроков, без серий' }
  ];
}

function renderCell(id: string, r: PharmacyRow, origin: Origin | null): React.ReactNode {
  const p = r.pharmacy;
  switch (id) {
    case 'pharmacy': return <PharmacyName pharmacy={p} />;
    case 'region': return <Value text={p.region} />;
    case 'address':
      return p.address
        ? <Typography title={p.address} sx={{ fontSize: 12.5, color: '#475569', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.address}</Typography>
        : <Value text={null} />;
    case 'phone': return <Value text={p.phone} sx={{ fontFamily: 'monospace' }} />;
    case 'hours': return <HoursCell pharmacy={p} />;
    case 'distance':
      return origin && r.distance !== null && hasCoords(p)
        ? <RouteLink from={origin} to={p} label={fmtDistance(r.distance)} />
        : <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap', color: '#94a3b8' }}>{fmtDistance(null)}</Typography>;
    case 'positions': return fmtInt(r.positions);
    case 'units': return <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtQty(r.units)}</Typography>;
    case 'amount': return <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtMoney(r.amount)}</Typography>;
    case 'expiry': return <ExpiryCell nearest={r.nearest} expiredQty={r.expiredQty} />;
    default: return null;
  }
}

/** Аптеки с остатком найденных (выбранных) товаров; клик по строке - карточка аптеки */
const PharmaciesTable: React.FC<Props> = ({
  rows, total, sort, loading, withSums, withContacts, origin, hasFilters, onSort, onOpen, onResetFilters
}) => {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const columns = columnsFor(withSums, withContacts, origin !== null);
  const minWidth = columns.reduce((acc, c) => acc + c.width, ACTIONS_WIDTH);
  const pages = Math.max(1, Math.ceil(rows.length / limit));

  // новый поиск, фильтр или сортировка - с первой страницы
  useEffect(() => { setPage(0); }, [rows]);
  const current = Math.min(page, pages - 1);
  const shown = rows.slice(current * limit, current * limit + limit);

  return (
    <Paper variant="outlined" sx={panelSx}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      <Box sx={{ px: 2, py: 0.75, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
          Аптек <Box component="b" sx={{ color: '#0f172a' }}>{fmtInt(rows.length)}</Box>
          {rows.length !== total && <> из {fmtInt(total)} (с учётом фильтров)</>}
        </Typography>
      </Box>

      <TableContainer sx={tableContainerSx}>
        <Table size="small" stickyHeader sx={{ minWidth, tableLayout: 'fixed', opacity: loading && rows.length ? 0.6 : 1, transition: 'opacity .15s' }}>
          <TableHead>
            <TableRow>
              {columns.map(col => {
                const label = col.sort ? (
                  <TableSortLabel active={sort.key === col.sort} direction={sort.key === col.sort ? sort.dir : 'asc'} onClick={() => onSort(col.sort as PharmacySortKey)}>
                    {col.label}
                  </TableSortLabel>
                ) : col.label;
                return (
                  <TableCell key={col.id} align={col.align} sx={{ ...headCellSx, width: col.width }}>
                    {col.tip ? <Tooltip title={col.tip} arrow><span>{label}</span></Tooltip> : label}
                  </TableCell>
                );
              })}
              <TableCell sx={{ ...headCellSx, width: ACTIONS_WIDTH }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map(r => (
              <TableRow key={r.pharmacy.guid} hover onClick={() => onOpen(r.pharmacy.guid)} sx={{ ...rowSx, cursor: 'pointer' }}>
                {columns.map(col => (
                  <TableCell key={col.id} align={col.align} sx={{ overflow: 'hidden' }}>{renderCell(col.id, r, origin)}</TableCell>
                ))}
                <TableCell align="center" sx={{ px: 0.5 }}>
                  <Tooltip title="Карточка аптеки">
                    <IconButton
                      size="small" aria-label="Карточка аптеки" sx={{ color: '#94a3b8' }}
                      onClick={e => { e.stopPropagation(); onOpen(r.pharmacy.guid); }}
                    >
                      <OpenIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">
                    {hasFilters ? 'По заданным фильтрам аптек не найдено' : 'Найденных товаров нет в наличии в аптеках вашей зоны'}
                  </Typography>
                  {hasFilters && (
                    <Typography
                      variant="body2" onClick={onResetFilters}
                      sx={{ mt: 0.5, color: 'primary.main', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Сбросить фильтры
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {rows.length > PAGE_SIZES[0] && (
        <TablePagination
          component="div" count={rows.length} page={current} rowsPerPage={limit} rowsPerPageOptions={PAGE_SIZES}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setLimit(Number(e.target.value)); setPage(0); }}
          labelRowsPerPage="На странице:"
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString('ru-RU')}–${to.toLocaleString('ru-RU')} из ${count.toLocaleString('ru-RU')}`}
          sx={{ borderTop: `1px solid ${BORDER}` }}
        />
      )}
    </Paper>
  );
};

export default React.memo(PharmaciesTable);
